'use client';
import React, { useState, useRef } from 'react';
import { Shield, CheckCircle, AlertTriangle, Clock, Upload, User, Mail, Phone, MapPin, Building2, FileText, Download, Eye, ChevronRight, AlertCircle, RefreshCw, Edit3, Save, Bell, Calendar, Info, XCircle, Paperclip } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

import MetricCard from '@/components/ui/MetricCard';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

interface ComplianceHold {
  id: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  raisedDate: string;
  resolvedBy?: string;
  status: 'active' | 'resolved' | 'pending-review';
  actionRequired?: string;
}

interface UploadedDoc {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadedDate: string;
  status: 'verified' | 'pending' | 'rejected';
  rejectionReason?: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const kycStatus = {
  overallStatus: 'approved' as const,
  verifiedDate: '2026-02-01',
  nextReviewDate: '2027-02-01',
  investorClass: 'Sophisticated Investor',
  amlStatus: 'cleared',
  fatcaStatus: 'on-file',
  suitabilityScore: 92,
  checks: [
    { label: 'Identity Verification', status: 'passed', date: '2026-02-01', detail: 'Passport + Proof of Address verified' },
    { label: 'AML Screening', status: 'passed', date: '2026-02-01', detail: 'No PEP/sanctions matches found' },
    { label: 'Source of Funds', status: 'passed', date: '2026-02-01', detail: 'Bank statements reviewed & cleared' },
    { label: 'Suitability Assessment', status: 'passed', date: '2026-02-01', detail: 'Score: 92/100 — Sophisticated Investor' },
    { label: 'FATCA / CRS Declaration', status: 'passed', date: '2025-11-15', detail: 'Self-certification on file' },
    { label: 'Annual Review', status: 'due', date: '2027-02-01', detail: 'Next scheduled review' },
  ],
};

const complianceHolds: ComplianceHold[] = [
  {
    id: 'hold-001',
    type: 'Document Expiry',
    severity: 'warning',
    title: 'Passport Expiry Warning',
    description: 'Your passport on file expires in 87 days (15 Nov 2026). Please upload a renewed passport to avoid account restrictions.',
    raisedDate: '2026-08-20',
    status: 'active',
    actionRequired: 'Upload renewed passport before 15 Nov 2026',
  },
  {
    id: 'hold-002',
    type: 'Suitability',
    severity: 'info',
    title: 'Annual Suitability Re-Assessment Due',
    description: 'Your annual suitability questionnaire is due for renewal. Complete it to maintain Sophisticated Investor classification.',
    raisedDate: '2026-08-01',
    status: 'pending-review',
    actionRequired: 'Complete suitability questionnaire',
  },
  {
    id: 'hold-003',
    type: 'AML',
    severity: 'critical',
    title: 'Enhanced Due Diligence Required',
    description: 'A recent transaction pattern triggered an EDD review. Your account has a temporary investment hold pending compliance officer review.',
    raisedDate: '2026-08-18',
    status: 'active',
    actionRequired: 'Await compliance officer contact within 2 business days',
  },
  {
    id: 'hold-004',
    type: 'Document',
    severity: 'info',
    title: 'Updated Proof of Address Required',
    description: 'Your proof of address document is older than 3 months. Please upload a recent utility bill or bank statement.',
    raisedDate: '2026-07-15',
    status: 'resolved',
    resolvedBy: 'Fatima Al-Rashidi',
  },
];

const uploadedDocs: UploadedDoc[] = [
  { id: 'udoc-001', name: 'Passport — Alexandra Chen', type: 'Identity', size: '2.4 MB', uploadedDate: '2026-02-01', status: 'verified' },
  { id: 'udoc-002', name: 'Proof of Address — Bank Statement Jan 2026', type: 'Address', size: '1.1 MB', uploadedDate: '2026-02-01', status: 'verified' },
  { id: 'udoc-003', name: 'Source of Funds — Investment Portfolio Statement', type: 'Source of Funds', size: '3.8 MB', uploadedDate: '2026-02-01', status: 'verified' },
  { id: 'udoc-004', name: 'FATCA Self-Certification Form', type: 'Tax', size: '0.4 MB', uploadedDate: '2025-11-15', status: 'verified' },
  { id: 'udoc-005', name: 'Suitability Questionnaire 2026', type: 'Suitability', size: '0.8 MB', uploadedDate: '2026-02-01', status: 'verified' },
  { id: 'udoc-006', name: 'Proof of Address — Utility Bill Aug 2026', type: 'Address', size: '0.9 MB', uploadedDate: '2026-08-10', status: 'pending' },
];

const accountDetails = {
  fullName: 'Alexandra Chen',
  email: 'alexandra.chen@meridianwealth.com',
  phone: '+971 50 234 5678',
  nationality: 'Singaporean',
  residency: 'UAE (Dubai)',
  address: 'Unit 2204, DIFC Gate District, Dubai, UAE',
  investorId: 'INV-2024-00847',
  accountType: 'Individual — Sophisticated Investor',
  onboardedDate: '2024-11-12',
  relationship: 'Relationship Manager: James Okonkwo',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function KYCStatusCard() {
  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-primary" />
          <h3 className="font-semibold text-foreground text-sm">KYC / Compliance Status</h3>
        </div>
        <StatusBadge variant="kyc-approved" label="Approved" dot />
      </div>
      <div className="p-5">
        {/* Summary row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Investor Class', value: kycStatus.investorClass, color: 'text-primary' },
            { label: 'AML Status', value: 'Cleared', color: 'text-positive' },
            { label: 'FATCA/CRS', value: 'On File', color: 'text-positive' },
            { label: 'Suitability Score', value: `${kycStatus.suitabilityScore}/100`, color: 'text-accent' },
          ].map((item) => (
            <div key={item.label} className="bg-surface-3 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
              <p className={`font-semibold text-sm font-mono-data ${item.color}`}>{item.value}</p>
            </div>
          ))}
        </div>

        {/* Check list */}
        <div className="space-y-2">
          {kycStatus.checks.map((check, i) => (
            <div key={`check-${i}`} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
              <div className="flex-shrink-0 mt-0.5">
                {check.status === 'passed' ? (
                  <CheckCircle size={15} className="text-positive" />
                ) : (
                  <Clock size={15} className="text-warning" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{check.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{check.detail}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-mono-data text-muted-foreground">{check.date}</p>
                <p className={`text-xs font-semibold mt-0.5 ${check.status === 'passed' ? 'text-positive' : 'text-warning'}`}>
                  {check.status === 'passed' ? 'Passed' : 'Upcoming'}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground bg-surface-3 rounded-lg px-3 py-2">
          <span>Verified: {kycStatus.verifiedDate}</span>
          <span className="flex items-center gap-1">
            <Calendar size={11} />
            Next review: {kycStatus.nextReviewDate}
          </span>
        </div>
      </div>
    </div>
  );
}

function ComplianceHoldsPanel() {
  const [filter, setFilter] = useState<'all' | 'active' | 'resolved'>('all');
  const activeHolds = complianceHolds.filter((h) => h.status === 'active').length;
  const filtered = complianceHolds.filter((h) => filter === 'all' ? true : filter === 'active' ? h.status !== 'resolved' : h.status === 'resolved');

  const severityConfig = {
    critical: { icon: XCircle, color: 'text-critical', bg: 'bg-critical/10 border-critical/30', label: 'Critical' },
    warning: { icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10 border-warning/30', label: 'Warning' },
    info: { icon: Info, color: 'text-info', bg: 'bg-info/10 border-info/30', label: 'Info' },
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-warning" />
          <h3 className="font-semibold text-foreground text-sm">Compliance Hold Notifications</h3>
          {activeHolds > 0 && (
            <span className="bg-critical/20 text-critical text-xs font-mono-data px-1.5 py-0.5 rounded-full">{activeHolds} active</span>
          )}
        </div>
        <div className="flex gap-1">
          {(['all', 'active', 'resolved'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-2.5 py-1 rounded-md capitalize transition-colors ${filter === f ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-border/50">
        {filtered.map((hold) => {
          const cfg = severityConfig[hold.severity];
          const Icon = cfg.icon;
          return (
            <div key={hold.id} className={`p-4 ${hold.status === 'resolved' ? 'opacity-60' : ''}`}>
              <div className={`rounded-lg border p-3 ${cfg.bg}`}>
                <div className="flex items-start gap-3">
                  <Icon size={15} className={`${cfg.color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-sm text-foreground">{hold.title}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${cfg.color} bg-black/20`}>{hold.type}</span>
                      {hold.status === 'resolved' && (
                        <span className="text-xs text-positive bg-positive/10 px-1.5 py-0.5 rounded">Resolved</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{hold.description}</p>
                    {hold.actionRequired && hold.status !== 'resolved' && (
                      <div className="mt-2 flex items-start gap-1.5">
                        <ChevronRight size={12} className={`${cfg.color} flex-shrink-0 mt-0.5`} />
                        <p className={`text-xs font-medium ${cfg.color}`}>{hold.actionRequired}</p>
                      </div>
                    )}
                    {hold.resolvedBy && (
                      <p className="text-xs text-muted-foreground mt-1">Resolved by: {hold.resolvedBy}</p>
                    )}
                  </div>
                  <p className="text-xs font-mono-data text-muted-foreground flex-shrink-0">{hold.raisedDate}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DocumentUploadPanel() {
  const [dragOver, setDragOver] = useState(false);
  const [uploadType, setUploadType] = useState('Identity');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const docTypes = ['Identity', 'Address', 'Source of Funds', 'Tax', 'Suitability', 'Other'];

  const statusConfig = {
    verified: { label: 'Verified', variant: 'confirmed' as const, color: 'text-positive' },
    pending: { label: 'Pending Review', variant: 'pending' as const, color: 'text-warning' },
    rejected: { label: 'Rejected', variant: 'failed' as const, color: 'text-critical' },
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <Upload size={16} className="text-accent" />
        <h3 className="font-semibold text-foreground text-sm">Additional Document Upload</h3>
      </div>
      <div className="p-5 space-y-5">
        {/* Upload zone */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <label className="text-xs font-medium text-muted-foreground">Document Type</label>
            <select
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value)}
              className="input-field text-xs py-1.5 w-auto flex-1 max-w-xs"
            >
              {docTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); }}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              dragOver
                ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-surface-3'
            }`}
          >
            <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" multiple />
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-surface-3 flex items-center justify-center">
                <Paperclip size={18} className="text-accent" />
              </div>
              <p className="text-sm font-medium text-foreground">Drop files here or click to browse</p>
              <p className="text-xs text-muted-foreground">PDF, JPG, PNG · Max 10 MB per file</p>
            </div>
          </div>
        </div>

        {/* Uploaded documents table */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Uploaded Documents</p>
          <div className="space-y-2">
            {uploadedDocs.map((doc) => {
              const cfg = statusConfig[doc.status];
              return (
                <div key={doc.id} className="flex items-center gap-3 bg-surface-3 rounded-lg px-3 py-2.5">
                  <FileText size={14} className="text-accent flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{doc.name}</p>
                    <p className="text-xs text-muted-foreground">{doc.type} · {doc.size} · {doc.uploadedDate}</p>
                  </div>
                  <StatusBadge variant={cfg.variant} label={cfg.label} dot />
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button className="btn-ghost p-1" title="View"><Eye size={13} /></button>
                    <button className="btn-ghost p-1" title="Download"><Download size={13} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountDetailsPanel() {
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(accountDetails.phone);
  const [address, setAddress] = useState(accountDetails.address);

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User size={16} className="text-accent" />
          <h3 className="font-semibold text-foreground text-sm">Account Details</h3>
        </div>
        <button
          onClick={() => setEditing(!editing)}
          className={editing ? 'btn-primary text-xs gap-1.5' : 'btn-secondary text-xs gap-1.5'}
        >
          {editing ? <><Save size={13} /> Save Changes</> : <><Edit3 size={13} /> Edit</>}
        </button>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Full Name', value: accountDetails.fullName, icon: User, editable: false },
            { label: 'Investor ID', value: accountDetails.investorId, icon: Shield, editable: false },
            { label: 'Email Address', value: accountDetails.email, icon: Mail, editable: false },
            { label: 'Account Type', value: accountDetails.accountType, icon: Building2, editable: false },
            { label: 'Nationality', value: accountDetails.nationality, icon: MapPin, editable: false },
            { label: 'Residency', value: accountDetails.residency, icon: MapPin, editable: false },
            { label: 'Onboarded', value: accountDetails.onboardedDate, icon: Calendar, editable: false },
            { label: 'Relationship', value: accountDetails.relationship, icon: User, editable: false },
          ].map((field) => (
            <div key={field.label} className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <field.icon size={11} />
                {field.label}
              </label>
              <p className="text-sm text-foreground font-medium bg-surface-3 rounded-lg px-3 py-2">{field.value}</p>
            </div>
          ))}

          {/* Editable fields */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Phone size={11} /> Phone Number
            </label>
            {editing ? (
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input-field text-sm"
              />
            ) : (
              <p className="text-sm text-foreground font-medium bg-surface-3 rounded-lg px-3 py-2">{phone}</p>
            )}
          </div>

          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <MapPin size={11} /> Registered Address
            </label>
            {editing ? (
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={2}
                className="input-field text-sm resize-none"
              />
            ) : (
              <p className="text-sm text-foreground font-medium bg-surface-3 rounded-lg px-3 py-2">{address}</p>
            )}
          </div>
        </div>

        {editing && (
          <div className="mt-4 alert-banner alert-warning text-xs">
            <AlertCircle size={14} className="text-warning flex-shrink-0" />
            <p>Address and phone changes require compliance re-verification. A compliance officer will review within 2 business days.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

export default function InvestorAccountScreen() {
  const [activeTab, setActiveTab] = useState<'kyc' | 'documents' | 'account' | 'holds'>('kyc');
  const activeHolds = complianceHolds.filter((h) => h.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">My Account & Compliance</h1>
          <p className="text-sm text-muted-foreground mt-1">KYC status, document management, and compliance notifications for INV-2024-00847</p>
        </div>
        <button className="btn-secondary text-xs gap-1.5">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Critical hold banner */}
      {activeHolds > 0 && (
        <div className="alert-banner alert-critical">
          <XCircle size={16} className="text-critical flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">{activeHolds} Active Compliance Hold{activeHolds > 1 ? 's' : ''}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your account has active compliance holds that may restrict investment activity. Review the Holds tab for details.
            </p>
          </div>
          <button onClick={() => setActiveTab('holds')} className="ml-auto btn-secondary text-xs gap-1 flex-shrink-0">
            View Holds <ChevronRight size={12} />
          </button>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="KYC Status" value="Approved" subValue="Verified 01 Feb 2026" variant="positive" icon={Shield} iconColor="text-positive" />
        <MetricCard label="Suitability Score" value="92/100" subValue="Sophisticated Investor" variant="info" icon={CheckCircle} iconColor="text-info" mono />
        <MetricCard label="Active Holds" value={String(activeHolds)} subValue={activeHolds > 0 ? 'Action required' : 'No active holds'} variant={activeHolds > 0 ? 'warning' : 'positive'} icon={Bell} iconColor={activeHolds > 0 ? 'text-warning' : 'text-positive'} />
        <MetricCard label="Documents on File" value={String(uploadedDocs.length)} subValue={`${uploadedDocs.filter(d => d.status === 'verified').length} verified`} variant="default" icon={FileText} iconColor="text-accent" />
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto">
        {[
          { key: 'kyc', label: 'KYC Status' },
          { key: 'holds', label: `Compliance Holds${activeHolds > 0 ? ` (${activeHolds})` : ''}` },
          { key: 'documents', label: 'Documents' },
          { key: 'account', label: 'Account Details' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`portal-tab ${activeTab === tab.key ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'kyc' && <KYCStatusCard />}
      {activeTab === 'holds' && <ComplianceHoldsPanel />}
      {activeTab === 'documents' && <DocumentUploadPanel />}
      {activeTab === 'account' && <AccountDetailsPanel />}
    </div>
  );
}
